import React, { useState, useMemo } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ScenarioBar from "../../components/ScenarioBar";
import { ROUTES, SCENARIOS, QUIZZES } from "../../utils/constants";
import { getScenarioNumByPath, getTimer } from "../../utils/helpers";
import { updateTime } from "../../redux/actions";
import { postAnswer } from "../../api/answer";
import {
  modalConfirm,
  modalQuestionResolution,
  modalHint,
  modalHintShow
} from "../../utils/logger";
import { IconHandClick } from "@tabler/icons-react";
import CheckButton from "../../components/CheckButton";
import estasseguro from "../../assets/bg/estasseguro-avatar-violet.png";
import respuestacorrecta from "../../assets/bg/correcto-avatar.png";
import respuestaincorrecta from "../../assets/bg/incorrecto-avatar.png";
import btnPistaViolet from "../../assets/btnPistaViolet.png";
import imageHint from "../../assets/hints/imageHintViolet.png";
import imageHintUsed from "../../assets/hintUsed.png";
import unIntento from "../../assets/un-intento-violet.png"
import dosIntentos from "../../assets/dos-intentos-violet.png"


// imágenes (no imprescindibles en el render, pero las dejamos por consistencia)
import imgRM from "../../assets/tests/scn2/rm-abdomen.jpg";
import imgTAC from "../../assets/tests/scn2/tac-abdomen.jpg";
import imgUS from "../../assets/tests/scn2/us-prostata.jpg";
import imgPET from "../../assets/tests/scn2/pet-psma.jpg";

function Scenario3({ intl }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const group = useSelector((state) => state.group);

  const activeScenario = getScenarioNumByPath(location.pathname);

  const QUIZ_ID = QUIZZES.ANTECEDENTES_TRATAMIENTO;
  const SCENARIO_ID = SCENARIOS.SCENARIO_3;

  const [selectedOptions, setSelectedOptions] = useState([]);
  const [isCompleted, setIsCompleted] = useState(false);
  const [hintUsed, setHintUsed] = useState(false);
  const [hintPenalty, setHintPenalty] = useState(0);
  const [hintAppliedToScore, setHintAppliedToScore] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 2 intentos con envío único (suma final)
  const [attempt, setAttempt] = useState(1);              // 1..2
  const [blockedOptions, setBlockedOptions] = useState(new Set());
  const [pendingScore, setPendingScore] = useState(0);    // acumulado a enviar

  const NUMBERATTEMPTS = 2;
  const CORRECT_ANSWER_POINTS = 10;
  const WRONG_ANSWER_POINTS = -5;
  const CORRECT_ANSWER_POINTS_2 = 3;
  const WRONG_ANSWER_POINTS_2 = 0;

  const options = [
    { id: 1, text: "<h4 class='txtSemiBlack bold text-center mt-2'>Diabetes mellitus</h4>", img: imgRM,  correct: true,  explanation: "-----" },
    { id: 2, text: "<h4 class='txtSemiBlack bold text-center mt-2'>HAS</h4>",                    img: imgTAC, correct: true,  explanation: "-----" },
    { id: 3, text: "<h4 class='txtSemiBlack bold text-center mt-2'>Dislipidemia</h4>",           img: imgUS,  correct: true,  explanation: "-----" },
    { id: 4, text: "<h4 class='txtSemiBlack bold text-center mt-2'>Antecedentes cardiovasculares</h4>", img: imgPET, correct: true,  explanation: "-----" },
    { id: 5, text: "<h4 class='txtSemiBlack bold text-center mt-2'>Índice de masa corporal</h4>", img: imgPET, correct: false, explanation: "-----" },
    { id: 6, text: "<h4 class='txtSemiBlack bold text-center mt-2'>AHF cáncer</h4>",             img: imgPET, correct: false, explanation: "-----" },
    { id: 7, text: "<h4 class='txtSemiBlack bold text-center mt-2'>Nivel de vitamina D</h4>",    img: imgPET, correct: false, explanation: "-----" },
    { id: 8, text: "<h4 class='txtSemiBlack bold text-center mt-2'>Índice de Gleason menor de 8</h4>", img: imgPET, correct: false, explanation: "-----" },
  ];

  const correctIds = options.filter(o => o.correct).map(o => o.id); // [1,2,3,4]

  const toggleOption = (id) => {
    if (blockedOptions.has(id)) return; // no permitir seleccionar bloqueadas
    setSelectedOptions(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  async function submitAnswer(finalScore, correctFinal) {
    const totalTime = getTimer();
    dispatch(updateTime(totalTime, 1));

    try {
      const res = await postAnswer({
        scenario: SCENARIO_ID,
        quiz: QUIZ_ID,
        group: group?._id,
        score: finalScore, // único envío con la suma final
        time: totalTime,
        lastVisited: ROUTES.SCENARIO3_EVALUACION,
        correct: correctFinal
      });

      if (res.status === 201) {
        setIsCompleted(true);
        navigate(ROUTES.SCENARIO3_EVALUACION);
      }
    } catch (error) {
      const status = error?.response?.status;
      const code = error?.response?.data?.code;

      if (status === 409 && code === "E_DUPLICATE_ANSWER") {
        await modalQuestionResolution({
          correct: false,
          question: "Respuesta ya registrada",
          textHtml: `<div class="txt32 txtBlue bold">Esta pregunta ya se había contestado con anterioridad y no se contabilizará, puedes continuar con el resto del juego.</div>`,
          customConfirmButtonHtml: "Ir a la conclusión",
        });
        navigate(ROUTES.SCENARIO3_EVALUACION);
        return;
      }

      await modalQuestionResolution({
        correct: false,
        question: "No se pudo guardar",
        textHtml: `<div class="txt24 txt-red bold">Ha ocurrido un problema al registrar tu respuesta. Inténtalo de nuevo</div>`,
        customConfirmButtonHtml: "Aceptar",
      });
      setIsSubmitting(false);
    }
  }

  const useHint = async () => {
    const hint = 'Al seleccionar un tratamiento sistémico intensivo, es crucial evaluar la capacidad del paciente para tolerarlo. Enfócate en los antecedentes que podrían aumentar el riesgo de eventos adversos cardiovasculares o metabólicos, que son preocupaciones conocidas con algunas de estas terapias. Los factores modificables del estado de salud general también son clave.';
    if (hintUsed) {
      await modalHintShow({ hint, imageHintUsed, color: "violet" });
      return;
    }
    const { used } = await modalHint({ image: imageHint, imageHintUsed, hint, color: "violet" });
    if (used && !hintUsed) {
      setHintUsed(true);
      setHintPenalty(3); // se aplicará 1 sola vez en el primer intento que compute puntos
    }
  };

  function checkAnswer() {
    if (selectedOptions.length === 0) {
      modalQuestionResolution({
        correct: false,
        textHtml: `<img src=${estasseguro} alt="Debes seleccionar las opciones que consideres" class="estasseguro" />
                   <div class="text-center txtTitulos mt-3 violet">Debes seleccionar las opciones que consideres</div>`,
      });
      return;
    }

    // Correcto si coincide EXACTAMENTE el conjunto de seleccionadas con las correctas (1,2,3,4)
    const isCorrect = correctIds.length === selectedOptions.length &&
                      correctIds.every(id => selectedOptions.includes(id));

    modalConfirm({ estasseguro, color: "violet" }).then((res) => {
      if (!res.isConfirmed) return;

      setIsSubmitting(true);
      const hintThisAttempt = (hintUsed && !hintAppliedToScore) ? hintPenalty : 0;

      if (isCorrect) {
        // ✅ Correcto (1er o 2º intento)
        const base = (attempt === 1) ? CORRECT_ANSWER_POINTS : CORRECT_ANSWER_POINTS_2;
        const attemptDelta = base - hintThisAttempt;
        const newScore = pendingScore + attemptDelta;
        if (hintThisAttempt) setHintAppliedToScore(true);

        modalQuestionResolution({
          correct: true,
          question: "Tus respuestas",
          textHtml: `<img src=${respuestacorrecta} alt="Respuesta correcta" style='max-width:450px' />
                     <div class="txt32 bold pt-4">Ganaste ${attemptDelta} puntos en este intento${hintThisAttempt ? " (-3 pista)" : ""}</div>
                     ${attempt === 2 ? `<div class="txt20 mt-2">Correcto en el segundo intento</div>` : ""}
                     <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>`,
          customConfirmButtonHtml: "Continuar",
          color: "violet"
        }).then(() => {
          submitAnswer(newScore, true); // envío único final
        });
        return;
      }

      // ❌ Incorrecto
      if (attempt < NUMBERATTEMPTS) {
        // 1er fallo: restamos -5 (y -3 si aplica pista), bloqueamos SOLO las opciones mal seleccionadas
        const wrongSelected = selectedOptions.filter(id => !correctIds.includes(id));
        const attemptDelta = WRONG_ANSWER_POINTS - hintThisAttempt; // -5 o -8 si la pista se aplica aquí
        const newScore = pendingScore + attemptDelta;
        if (hintThisAttempt) setHintAppliedToScore(true);

        modalQuestionResolution({
          correct: false,
          question: "Tus respuestas",
          textHtml: `<img src=${respuestaincorrecta} alt="Respuesta incorrecta" style='max-width:450px' />
                     <div class="txt28 bold pt-3">Respuesta incorrecta ${attemptDelta} puntos${hintThisAttempt ? " (incluye -3 por pista)" : ""}</div>
                     <div class="txt22 mt-2">Te queda <b>1</b> intento. Revisa y vuelve a elegir.</div>
                     <div class="txt20 mt-2">Acumulado hasta ahora: <b>${newScore}</b></div>`,
          customConfirmButtonHtml: "Intentar de nuevo",
          color: "violet"
        }).then(() => {
          setPendingScore(newScore);
          setBlockedOptions(prev => {
            const next = new Set(prev);
            wrongSelected.forEach(id => next.add(id));
            return next;
          });
          setAttempt(attempt + 1);
          setSelectedOptions([]); // empezamos limpio el 2º intento
          setIsSubmitting(false);
        });
        return;
      }

      // 2º fallo: delta 0 (o -3 si justo aquí aplicamos la pista por primera vez)
      const attemptDelta = WRONG_ANSWER_POINTS_2 - hintThisAttempt; // 0 o -3
      const newScore = pendingScore + attemptDelta;
      if (hintThisAttempt) setHintAppliedToScore(true);

      modalQuestionResolution({
        correct: false,
        question: "Tus respuestas",
        textHtml: `<img src=${respuestaincorrecta} alt="Respuesta incorrecta" style='max-width:450px' />
                   <div class="txt32 bold pt-4">Has agotado los intentos</div>
                   ${hintThisAttempt ? `<div class="txt20 mt-2">Se aplicó -3 por pista en este intento</div>` : ""}
                   <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>`,
        customConfirmButtonHtml: "Continuar",
        color: "violet"
      }).then(() => {
        submitAnswer(newScore, false); // envío único final
      });
    });
  }

  const remainingAttempts = NUMBERATTEMPTS - attempt + 1;
      
  const shuffledOptions = useMemo(() => {
    return [...options].sort(() => Math.random() - 0.5);
  }, []);

  return (
    <div className="h-100">
      <div className="container-fluid px-5 h-100 d-flex flex-column justify-content-center bgCircleViolet2">
        <h3 className="txt30 violet bold" style={{ backgroundColor: "#FFFFFFAA", borderRadius: "10px" }}>
          Para hacer una selección adecuada del tratamiento debemos considerar varios aspectos. ¿Qué antecedentes o comorbilidades del paciente tendría en cuenta para la selección del tratamiento?
        </h3>
        <div className="d-flex justify-content-between">
          <h5 className="mb-4 txt32 text-left my-2 violet">
            <IconHandClick size={40} color="#8c3888" stroke={1} /> Selecciona las respuestas correctas
          </h5>
          <div className="mb-3">
            <img src={remainingAttempts == 2 ? dosIntentos : unIntento} className="imgIntentos" />
          </div>
        </div>

        <div className="row g-4">
          {shuffledOptions.map((option) => {
            const isSelected = selectedOptions.includes(option.id);
            const isBlocked = blockedOptions.has(option.id);
            return (
              <div key={option.id} className="col-md-3">
                <div
                  className={`position-relative cursor-pointer p-3 rounded shadow h-100 d-flex justify-content-center align-items-center ${isSelected ? "selected" : ""} ${isBlocked ? "opacity-50" : ""}`}
                  style={{
                    border: isSelected ? "1px solid #8c388855" : "1px solid #fff",
                    backgroundColor: isSelected ? "#8c388855" : "#fff",
                    pointerEvents: isBlocked ? "none" : "auto",
                  }}
                  onClick={() => toggleOption(option.id)}
                  aria-disabled={isBlocked}
                  title={isBlocked ? "Opción bloqueada (ya fue incorrecta)" : "Seleccionar"}
                >
                  <div className="txt18" dangerouslySetInnerHTML={{ __html: option.text }} />
                  {isBlocked && (
                    <span className="badge bg-secondary position-absolute top-0 end-0 m-2">Bloqueada</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="d-flex justify-content-between mt-4">
          <div className="position-relative d-inline-block">
            <img
              src={btnPistaViolet}
              className={`btnPista mt-2 ${hintUsed ? "used" : ""}`}
              onClick={useHint}
              alt={hintUsed ? "Pista usada" : "Usar pista"}
              title={hintUsed ? "Pista usada (-3)" : "Usar pista (-3 puntos)"}
            />
            {hintUsed && <span className="badge-penalty">-3</span>}
          </div>
          <CheckButton
            text="Comprobar respuesta"
            onClick={checkAnswer}
            disabled={isCompleted || isSubmitting}
            color="violet"
            loading={isSubmitting}
          />
        </div>
      </div>

      <ScenarioBar activeScenario={activeScenario} />
    </div>
  );
}

export default injectIntl(Scenario3);
